import {
  AccessTypes,
  AvailablePaymentMethods,
  CityConfigs,
  MerchantConfig,
  ServiceAreaConfig
} from '../models/configs';
import {
  AccessTypeDto,
  CityConfigsDto,
  MerchantConfigDTO,
  MerchantStatus,
  PaymentMethodTypeTransporter,
  ServiceAreaConfigDto
} from '../dtos/configs';
import { DeliveryAction } from '../models/deliveries';

export function dtoToCityConfigs({ configs }: CityConfigsDto): CityConfigs {
  const transformedConfigs: CityConfigs = {};
  if (!Object.keys(configs).length) {
    return transformedConfigs;
  }

  for (const [cityId, config] of Object.entries(configs)) {
    transformedConfigs[cityId] = {
      cityId: config.city_id,
      isDropOffRequired: config.is_dropoff_required,
      landingPage: config.landing_page
    };
  }

  return transformedConfigs;
}

export function dtoToServiceAreaConfig(config: ServiceAreaConfigDto[] = []): ServiceAreaConfig[] {
  return config.map(item => ({
    serviceAreaId: item.service_area_id,
    bannerText: item.banner_text
  }));
}

const isCustomFulfillmentActive = (
  isEnabled?: boolean,
  distance?: number,
  startsAt?: number,
  endsAt?: number
): boolean => {
  if (!isEnabled || !startsAt || !endsAt || Number.isNaN(distance)) {
    return false;
  }
  const timeNow = new Date().getTime();
  const startsAtMs = startsAt * 1000;
  const endsAtMs = endsAt * 1000;

  return Boolean(timeNow > startsAtMs && timeNow < endsAtMs);
};

export const transformAvailablePaymentMethods = (
  availablePaymentMethods: PaymentMethodTypeTransporter[]
): AvailablePaymentMethods => {
  const availablePayments: AvailablePaymentMethods = {
    isInvoiceEnabled: false,
    isCardEnabled: false,
    isWalletEnabled: false
  };

  availablePaymentMethods.some(item => item === PaymentMethodTypeTransporter.INVOICE)
    ? (availablePayments.isInvoiceEnabled = true)
    : (availablePayments.isInvoiceEnabled = false);
  availablePaymentMethods.some(item => item === PaymentMethodTypeTransporter.WALLET)
    ? (availablePayments.isWalletEnabled = true)
    : (availablePayments.isWalletEnabled = false);
  availablePaymentMethods.some(item => item === PaymentMethodTypeTransporter.CARD)
    ? (availablePayments.isCardEnabled = true)
    : (availablePayments.isCardEnabled = false);

  return availablePayments;
};

export const transformAccessTypes = (accessTypes: AccessTypeDto[]): AccessTypes => {
  const enabledAccessTypes: AccessTypes = {
    isApiAccessEnabled: false,
    isPortalAccessEnabled: false
  };
  accessTypes.some(item => item === AccessTypeDto.API)
    ? (enabledAccessTypes.isApiAccessEnabled = true)
    : (enabledAccessTypes.isApiAccessEnabled = false);
  accessTypes.some(item => item === AccessTypeDto.PORTAL)
    ? (enabledAccessTypes.isPortalAccessEnabled = true)
    : (enabledAccessTypes.isPortalAccessEnabled = false);

  return enabledAccessTypes;
};

export function dtoToMerchantConfig(datastoreDto: MerchantConfigDTO): MerchantConfig {
  const {
    status,
    contact_details,
    is_otp_enabled,
    is_customer_phone_number_mandatory,
    is_drop_off_mandatory,
    fulfillment_configurations,
    payment_configurations,
    fulfillment_types,
    access_types,
    is_orderId_in_order_reference_mandatory,
    default_delivery_instruction
  } = datastoreDto;

  const { preferred_contact_number } = contact_details;
  const {
    standard_fulfillment_distance,
    custom_fulfillment_distance,
    is_cfd_enabled,
    cfd_end_timestamp,
    cfd_start_timestamp
  } = fulfillment_configurations;
  const {
    available_payment_methods: availablePaymentMethodsRaw,
    primary_payment_method: primaryPaymentMethod,
    secondary_payment_method: secondaryPaymentMethod
  } = payment_configurations;

  const isCustomFulfillmentDistanceActive = isCustomFulfillmentActive(
    is_cfd_enabled,
    custom_fulfillment_distance,
    cfd_start_timestamp,
    cfd_end_timestamp
  );

  const fallBackDeliveryInstruction = DeliveryAction.CALL_MERCHANT;

  return {
    preferredContactNumber: preferred_contact_number,
    isMerchantEnabled: status === MerchantStatus.ENABLED,
    isOtpEnabled: Boolean(is_otp_enabled),
    isCustomerPhoneNumberMandatory: Boolean(is_customer_phone_number_mandatory),
    isDropOffMandatory: Boolean(is_drop_off_mandatory),
    standardFulfillmentDistance: standard_fulfillment_distance,
    availablePaymentMethods: transformAvailablePaymentMethods(availablePaymentMethodsRaw),
    primaryPaymentMethod,
    secondaryPaymentMethod,
    fulfillmentTypes: fulfillment_types,
    accessTypes: transformAccessTypes(access_types),
    isCustomFulfillmentDistanceActive,
    customFulfillmentDistance: custom_fulfillment_distance,
    cfdEndsAt: isCustomFulfillmentDistanceActive ? cfd_end_timestamp! * 1000 : undefined,
    isOrderIdMandatory: Boolean(is_orderId_in_order_reference_mandatory),
    defaultDeliveryAction: default_delivery_instruction || fallBackDeliveryInstruction
  };
}
